import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';

/**
 * Phase 5.G PR-5 — compact per-row open/close button used on the
 * MobileGateCard. Mirrors GateControlPanel's command lifecycle (confirm
 * → POST → poll for ack/timeout → reset) but renders as a small chip
 * that fits inside a card footer instead of taking the whole panel
 * width with verbose status pills.
 *
 * Behavioural parity with GateControlPanel — same API endpoint
 * (``POST /api/customer/gates/<id>/commands``), same confirmation
 * dialog content, same 30s server-side timeout. Only the visual is
 * different.
 */

const POLL_INTERVAL_MS = 1500;
const POLL_MAX_DURATION_MS = 35000;
const RESET_DELAY_MS = 4000;

function MobileGateCardOpenButton({ gate, canIssueCommands, onCommandComplete }) {
  // 'idle' | 'confirming' | 'sending' | 'polling' | 'done'
  const [phase, setPhase] = useState('idle');
  const [terminal, setTerminal] = useState(null);

  if (!canIssueCommands) return null;
  if (gate.installation_status === 'not_started') return null;

  const isOffline = !!gate.is_offline;
  const requestType = gate.is_open ? 'close' : 'open';
  const requestLabel = gate.is_open ? 'סגור' : 'פתח';

  const startConfirm = (e) => {
    e.stopPropagation();
    if (isOffline) return;
    setPhase('confirming');
    setTerminal(null);
  };

  const cancel = () => {
    setPhase('idle');
  };

  const send = async () => {
    setPhase('sending');
    try {
      const res = await axios.post(`/api/customer/gates/${gate.id}/commands`, { type: requestType });
      setPhase('polling');
      pollUntilTerminal(res.data.id);
    } catch (err) {
      const status = err.response?.status === 409 ? 'offline' : 'failed';
      setPhase('done');
      setTerminal({ status });
      scheduleReset();
    }
  };

  const pollUntilTerminal = (commandId) => {
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const res = await axios.get(`/api/customer/gates/${gate.id}/commands/${commandId}`);
        const cmd = res.data;
        if (['acknowledged', 'failed', 'timeout'].includes(cmd.status)) {
          setPhase('done');
          setTerminal({ status: cmd.status });
          scheduleReset();
          if (onCommandComplete) onCommandComplete(cmd);
          return;
        }
      } catch { /* keep polling silently */ }
      if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
        setPhase('done');
        setTerminal({ status: 'timeout' });
        scheduleReset();
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    setTimeout(tick, POLL_INTERVAL_MS);
  };

  const scheduleReset = () => {
    setTimeout(() => {
      setPhase('idle');
      setTerminal(null);
    }, RESET_DELAY_MS);
  };

  let content;
  let buttonClass = 'biz-gate-card-cmd-btn';
  let disabled = isOffline;

  if (phase === 'sending' || phase === 'polling') {
    content = <span className="biz-gate-card-cmd-spinner" aria-hidden="true" />;
    buttonClass += ' is-busy';
    disabled = true;
  } else if (phase === 'done' && terminal) {
    if (terminal.status === 'acknowledged') {
      content = <span aria-hidden="true">✓</span>;
      buttonClass += ' is-ok';
    } else if (terminal.status === 'offline') {
      content = <span aria-hidden="true">⚠</span>;
      buttonClass += ' is-warn';
    } else if (terminal.status === 'timeout') {
      content = <span aria-hidden="true">⏳</span>;
      buttonClass += ' is-warn';
    } else {
      content = <span aria-hidden="true">✕</span>;
      buttonClass += ' is-err';
    }
    disabled = true;
  } else {
    content = requestLabel;
    if (requestType === 'open') buttonClass += ' is-open-action';
    else buttonClass += ' is-close-action';
  }

  return (
    <>
      <button
        type="button"
        className={buttonClass}
        onClick={startConfirm}
        disabled={disabled}
        title={isOffline ? 'הרכיב לא מחובר — לא ניתן לשלוח פקודה' : ''}
        aria-label={`${requestLabel} שער ${gate.name}`}
      >
        {content}
      </button>

      {phase === 'confirming' && createPortal(
        <ConfirmDialog
          gateName={gate.name}
          requestType={requestType}
          onCancel={cancel}
          onConfirm={send}
        />,
        document.body
      )}
    </>
  );
}

function ConfirmDialog({ gateName, requestType, onCancel, onConfirm }) {
  const heading = requestType === 'open' ? 'אישור פתיחת שער' : 'אישור סגירת שער';
  const confirmLabel = requestType === 'open' ? 'כן, פתח' : 'כן, סגור';
  const confirmColor = requestType === 'open' ? '#dc2626' : '#1a1a2e';
  const message = requestType === 'open'
    ? `האם לפתוח את "${gateName}"? הפקודה תישלח מיידית לרכיב.`
    : `האם לסגור את "${gateName}"? הפקודה תישלח מיידית לרכיב.`;

  return (
    <>
      <div className="modal-overlay" onClick={onCancel} />
      <div
        className="modal"
        style={{
          position: 'fixed',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 101,
          width: 'min(420px, calc(100vw - 24px))',
          padding: '1.5rem',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: '0.6rem', fontSize: '1.05rem', fontWeight: 600 }}>{heading}</h2>
        <p style={{ color: '#4a5568', fontSize: '0.9rem', marginBottom: '1.25rem' }}>{message}</p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '5px',
              fontSize: '0.85rem',
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
              color: '#4a5568',
            }}
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            style={{
              padding: '0.5rem 1.1rem',
              background: confirmColor,
              border: 'none',
              borderRadius: '5px',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Heebo, sans-serif',
              color: '#ffffff',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

export default MobileGateCardOpenButton;
